import './styles/style.scss'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import 'react-toastify/dist/ReactToastify.css'
import { ToastContainer } from 'react-toastify'
import { CheckJWT } from './CheckJWT'
import ComingSoon from './components/common/comingSoon/comingSoon'
import Home from './pages/home/Home'
import Login from './pages/login/login'
import Rule from './pages/rule/Rule'
import Transaction from './pages/kyt/transaction/Transaction'
import ExternalPaymentMethods from './pages/kyt/externalPaymentMethods/ExternalPaymentMethods'
import TransactionDetail from './pages/kyt/transaction/TransactionDetail'
import User from './pages/kyt/user/User'
import UserDetail from './pages/kyt/user/UserDetail'
import { useDispatch, useSelector } from 'react-redux'
import { performLogin, performLogout } from './features/auth/loginSlice'
import Users from './pages/admin/users/Users'
import Roles from './pages/admin/roles/Roles'
import OppositionControl from './pages/admin/cpo/OppositionControl'
import Profile from './pages/profile/Profile'
import RoleDetails from './pages/admin/roles/RoleDetails'
import { performLogoutRoles } from './features/roles/rolesSlice'
import { performLogoutUsers } from './features/users/userSlice'
import { performLogoutTransactions } from './features/transactions/transactionSlice'
import { performLogoutRules } from './features/rules/rulesSlice'
import { performLogoutUserAdmin } from './features/usersAdmin/userAdminSlice'
import Reports from './pages/reports/reports'
import ReportRouter from './pages/reports/report/reportRouter'
import CaseManagement from './pages/case-management/CaseManagement'
import Case from './pages/case-management/cases/Case'
import SecurityParams from './pages/admin/securityParams/SecurityParams'
import AlertSettings from './pages/admin/alertSettings/AlertSettings'
import Onboarding from './pages/onboarding/Onboarding'
import OnboardingDetail from './pages/onboarding/OnboardingDetail'
import EcommerceRisk from './pages/ecommerceRisk/EcommerceRisk'
import EcommerceRiskDetail from './pages/ecommerceRisk/EcommerceRiskDetail'
import OnboardingManualCheck from './pages/onboarding/OnboardingManualCheck'
import AmlManualCheck from './pages/aml/AmlManualCheck'
import Aml from './pages/aml/Aml'
import AmlDetail from './pages/aml/AmlDetail'
import AmlCrypto from './pages/amlCrypto/AmlCrypto'
import AmlCryptoManualCheck from './pages/amlCrypto/AmlCryptoManualCheck'
import Webhooks from './pages/admin/webhooks/Webhooks'
import GovCheck from './pages/govCheck/GovCheck'
import GovCheckManualCheck from './pages/govCheck/GovCheckManualCheck'
import GovCheckDetail from './pages/govCheck/GovCheckDetail'
import Kyb from './pages/kyb/Kyb'
import KybDetailPerson from './pages/kyb/KybDetailPerson'
import Checklist from './pages/admin/checklist/Checklist'
import KybLists from './pages/kyb/KybLists'
import KybNotifications from './pages/kyb/KybNotifications'
import KybNotificationsV2 from './pages/kyb/KybNotificationsV2'
import { Suspense, useEffect } from 'react'
import Lists from './pages/admin/lists/Lists'
import { dsTheme } from './themes/theme-light'
import { dsThemeDark } from './themes/theme-dark'
import { nucleaTheme } from './themes/theme-nuclea'
import ReactGA from 'react-ga'
import DemoHome from './pages/home/DemoHome'
import KytManualCheck from './pages/kyt/manualCheck/KytManualCheck'
import Device from './pages/device/device'
import DeviceDetails from './pages/device/deviceDetails'
import WorkflowsBuilder from './pages/workflows/workflowsBuilder'
import WorkflowsOptions from './pages/workflows/workflowsOptions'
import WorkflowsAi from './pages/workflows/workflowsAi'
import Workflows from './pages/workflows/workflows'
import RiskMatrix from './pages/admin/riskMatrix/riskMatrix'
import RiskMatrixCreation from './pages/admin/riskMatrix/riskMatrixCreate'
import Batch from './pages/batch/Batch'
import BatchDetails from './pages/batch/batchDetails/BatchDetails'
import { LicenseInfo } from '@mui/x-license'
import SuspenseSpinner from './components/common/spinner/suspenseSpinner'
import { LoadScript } from '@react-google-maps/api'
import CaseWorkflows from './pages/case-management/caseWorkflows/CaseWorkflows'
import WorkflowCreation from './pages/case-management/caseWorkflows/CaseWorkflowCreate'
import ExternalPaymentMethodsDetail from './pages/kyt/externalPaymentMethods/ExternalPaymentMethodsDetail'
import KybDetailBusiness from './pages/kyb/KybDetailBusiness'
import { Kyc } from './pages/kyc'
import { KycPerson } from './pages/kyc/kycPerson'
import { KycV2 } from './pages/kycv2'
import { KycV2Person } from './pages/kycv2/person'
import { KycV2Session } from './pages/kycv2/session'
import KybManualCheck from './pages/kyb/KybManualCheck'
import AmlBr from './pages/aml/br/AmlBr'
import AmlBrDetail from './pages/aml/br/AmlBrDetail'
import CsvPage from './pages/admin/csv'
import Totalizadores from './pages/admin/totalizadores/Totalizadores'
import { KycChecks } from './pages/kyc/kycChecks'
import CasesPage from './pages/case-management/cases/CasesPage'
import CaseAdminPanel from './pages/case-management/adminPanel/CaseAdminPanel'
import KybSettingsPage from './pages/kyb/settings/KybSettingsPage'
import KybLoadInBatches from './pages/kyb/KybLoadInBatches'
import KymPersons from './pages/kym/KymPersons'
import KymBusiness from './pages/kym/KymBusiness'
import ClientConsumptions from './pages/admin/client-services/ClientConsumptions'
import BackOffice from './pages/backoffice/BackOffice'
import { datadogRum } from '@datadog/browser-rum'
import { cleanStoredClientKey, getClientKeyWithAccessToken } from './lib/security'

// Initialize Datadog RUM
datadogRum.init({
  applicationId: process.env.REACT_APP_DATADOG_APP_ID,
  clientToken: process.env.REACT_APP_DATADOG_TOKEN,
  site: 'us3.datadoghq.com',
  service: 'dashboard',
  env: process.env.REACT_APP_DATADOG_ENV,

  // version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  startSessionReplayRecordingManually: true,
  defaultPrivacyLevel: 'mask-user-input',
})

const ThemeMap = {
  light: dsTheme,
  dark: dsThemeDark,
  nuclea: nucleaTheme,
}

function App({ theme }) {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY)
  ReactGA.initialize('G-MJ3KBQ70LT')
  const { accessToken, activeServices } = useSelector((state) => state.login)
  const dispatch = useDispatch()

  const handleUserLogin = async (loggedUser) => {
    if (loggedUser) {
      dispatch(performLogin(loggedUser.access_token))
      // Start session replay recording
      datadogRum.startSessionReplayRecording()
    } else {
      // Stop Datadog RUM session
      datadogRum.stopSessionReplayRecording()
      dispatch(performLogout())
      dispatch(performLogoutRoles())
      dispatch(performLogoutUsers())
      dispatch(performLogoutTransactions())
      dispatch(performLogoutRules())
      dispatch(performLogoutUserAdmin())
      fetch(`${process.env.REACT_APP_BASEURL}/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'client-key': await getClientKeyWithAccessToken(accessToken),
        },
      }).then(() => {
        cleanStoredClientKey()
      })
    }
  }

  return (
    <Suspense fallback={<SuspenseSpinner />}>
      <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <ThemeProvider theme={ThemeMap[theme] || dsTheme}>
          <BrowserRouter>
            <Routes>
              <Route path="/">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : process.env.REACT_APP_IS_DEMO ? (
                      <CheckJWT
                        path={'/'}
                        activeServices={activeServices}
                        next={<DemoHome />}
                        name="overview"
                        handleUserLogout={handleUserLogin}
                      />
                    ) : (
                      <CheckJWT
                        path={'/'}
                        activeServices={activeServices}
                        next={<Home />}
                        name="overview"
                        handleUserLogout={handleUserLogin}
                      />
                    )
                  }
                />
              </Route>
              <Route path="/case-management">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/case-management'}
                        activeServices={activeServices}
                        next={<CaseManagement />}
                        handleUserLogout={handleUserLogin}
                        name="casesAndInvestigation"
                      />
                    )
                  }
                />
                <Route
                  path="case"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/case-management/case'}
                        activeServices={activeServices}
                        next={<CasesPage />}
                        handleUserLogout={handleUserLogin}
                        name="casesAndInvestigation"
                      />
                    )
                  }
                />
                <Route
                  path="admin"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/case-management/AdminPanel'}
                        activeServices={activeServices}
                        next={<CaseAdminPanel />}
                        handleUserLogout={handleUserLogin}
                        name="caseAdminPanel:emailClient"
                      />
                    )
                  }
                />
                <Route
                  path=":tab"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/case-management'}
                        activeServices={activeServices}
                        next={<CaseManagement />}
                        handleUserLogout={handleUserLogin}
                        name="casesAndInvestigation"
                      />
                    )
                  }
                />
              </Route>
              <Route
                path="/case-management/case/:id"
                element={
                  !accessToken ? (
                    <Login handleUserLogin={handleUserLogin} />
                  ) : (
                    <CheckJWT
                      path={'/case-management/case/:id'}
                      activeServices={activeServices}
                      next={<Case />}
                      handleUserLogout={handleUserLogin}
                      name="casesAndInvestigation"
                    />
                  )
                }
              />
              <Route path="/case-workflows">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/case-workflows'}
                        activeServices={activeServices}
                        next={<CaseWorkflows />}
                        handleUserLogout={handleUserLogin}
                        name="caseWorkflows"
                      />
                    )
                  }
                />
                <Route
                  path="creation"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/case-workflows'}
                        activeServices={activeServices}
                        next={<WorkflowCreation />}
                        handleUserLogout={handleUserLogin}
                        name="caseWorkflows"
                      />
                    )
                  }
                />
              </Route>
              <Route
                path="/case-workflows/:id"
                element={
                  !accessToken ? (
                    <Login handleUserLogin={handleUserLogin} />
                  ) : (
                    <CheckJWT
                      path={'/case-workflows/:id'}
                      activeServices={activeServices}
                      next={<WorkflowCreation />}
                      handleUserLogout={handleUserLogin}
                      name="caseWorkflows"
                    />
                  )
                }
              />
              <Route path="/onboardings">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/onboardings'}
                        activeServices={activeServices}
                        next={<Onboarding />}
                        handleUserLogout={handleUserLogin}
                        name="onboarding"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/onboardings/:id'}
                        activeServices={activeServices}
                        next={<OnboardingDetail />}
                        handleUserLogout={handleUserLogin}
                        name="onboarding"
                      />
                    )
                  }
                />
                <Route
                  path="manual"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/onboardings/manual'}
                        activeServices={activeServices}
                        next={<OnboardingManualCheck />}
                        handleUserLogout={handleUserLogin}
                        name="onboardingManual"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/kyb">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyb'}
                        activeServices={activeServices}
                        next={<Kyb />}
                        handleUserLogout={handleUserLogin}
                        name="kyb"
                      />
                    )
                  }
                />
                <Route
                  path=":tab"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyb'}
                        activeServices={activeServices}
                        next={<Kyb />}
                        handleUserLogout={handleUserLogin}
                        name="kyb"
                      />
                    )
                  }
                />
                <Route
                  path="batches"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyb/batches'}
                        activeServices={activeServices}
                        next={<KybLoadInBatches />}
                        handleUserLogout={handleUserLogin}
                        name="kyb"
                      />
                    )
                  }
                />
                <Route
                  path="settings"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyb/settings'}
                        activeServices={activeServices}
                        next={<KybSettingsPage />}
                        handleUserLogout={handleUserLogin}
                        name="kyb"
                      />
                    )
                  }
                />
                <Route
                  path=":country/detail/:id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyb/:country/detail/:id'}
                        activeServices={activeServices}
                        next={<KybDetailBusiness />}
                        handleUserLogout={handleUserLogin}
                        name="kyb"
                      />
                    )
                  }
                />
                <Route path="/kyb/lists">
                  <Route
                    index
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/kyb/lists'}
                          activeServices={activeServices}
                          next={<KybLists />}
                          handleUserLogout={handleUserLogin}
                          name="blacklists"
                        />
                      )
                    }
                  />
                  <Route
                    path=":tab"
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/kyb/lists'}
                          activeServices={activeServices}
                          next={<KybLists />}
                          handleUserLogout={handleUserLogin}
                          name="blacklists"
                        />
                      )
                    }
                  />
                </Route>
                {/* <Route path="/kyb/notifications">
                  <Route
                    index
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/kyb/notifications'}
                          activeServices={activeServices}
                          next={<KybNotifications />}
                          handleUserLogout={handleUserLogin}
                          name="companyNotifications"
                        />
                      )
                    }
                  />
                  <Route
                    path=":tab"
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/kyb/notifications'}
                          activeServices={activeServices}
                          next={<KybNotifications />}
                          handleUserLogout={handleUserLogin}
                          name="companyNotifications"
                        />
                      )
                    }
                  />
                </Route> */}
                <Route path="/kyb/notifications">
                  <Route
                    index
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/kyb/notifications'}
                          activeServices={activeServices}
                          next={<KybNotificationsV2 />}
                          handleUserLogout={handleUserLogin}
                          name="companyNotifications"
                        />
                      )
                    }
                  />
                  <Route
                    path=":tab"
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/kyb/notifications'}
                          activeServices={activeServices}
                          next={<KybNotificationsV2 />}
                          handleUserLogout={handleUserLogin}
                          name="companyNotifications"
                        />
                      )
                    }
                  />
                </Route>
              </Route>
              <Route
                path="/kyc/:country/detail/:id"
                element={
                  !accessToken ? (
                    <Login handleUserLogin={handleUserLogin} />
                  ) : (
                    <CheckJWT
                      path={'/kyc/:country/detail/:id'}
                      activeServices={activeServices}
                      next={<KybDetailPerson />}
                      handleUserLogout={handleUserLogin}
                      name="kyc"
                    />
                  )
                }
              />
              <Route
                path="/kyb/manual"
                element={
                  !accessToken ? (
                    <Login handleUserLogin={handleUserLogin} />
                  ) : (
                    <CheckJWT
                      path={'/kyb/manual'}
                      activeServices={activeServices}
                      next={<KybManualCheck />}
                      handleUserLogout={handleUserLogin}
                      name="kybManual"
                    />
                  )
                }
              />
              <Route path="/checklists">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/checklists'}
                        activeServices={activeServices}
                        next={<Checklist />}
                        handleUserLogout={handleUserLogin}
                        name="checklists"
                      />
                    )
                  }
                />
              </Route>
              <Route
                index
                element={
                  !accessToken ? (
                    <Login handleUserLogin={handleUserLogin} />
                  ) : (
                    <CheckJWT
                      path={'/checklists'}
                      activeServices={activeServices}
                      next={<Checklist />}
                      handleUserLogout={handleUserLogin}
                      name="checklists"
                    />
                  )
                }
              />
              <Route path="/checklists">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/checklists'}
                        activeServices={activeServices}
                        next={<Checklist />}
                        handleUserLogout={handleUserLogin}
                        name="checklists"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/aml">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/aml'}
                        activeServices={activeServices}
                        next={<Aml />}
                        handleUserLogout={handleUserLogin}
                        name="aml"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/aml/:id'}
                        activeServices={activeServices}
                        next={<AmlDetail />}
                        handleUserLogout={handleUserLogin}
                        name="aml"
                      />
                    )
                  }
                />
                <Route
                  path="manual"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/aml/manual'}
                        activeServices={activeServices}
                        next={<AmlManualCheck />}
                        handleUserLogout={handleUserLogin}
                        name="amlManual"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/br-aml">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/br-aml'}
                        activeServices={activeServices}
                        next={<AmlBr />}
                        handleUserLogout={handleUserLogin}
                        name="br"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/br-aml/:id'}
                        activeServices={activeServices}
                        next={<AmlBrDetail />}
                        handleUserLogout={handleUserLogin}
                        name="aml"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/aml-crypto">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/aml-crypto'}
                        activeServices={activeServices}
                        next={<AmlCrypto />}
                        handleUserLogout={handleUserLogin}
                        name="amlCrypto"
                      />
                    )
                  }
                />
                <Route
                  path="manual"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/aml-crypto/manual'}
                        activeServices={activeServices}
                        next={<AmlCryptoManualCheck />}
                        handleUserLogout={handleUserLogin}
                        name="amlCryptoManual"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/ecommerce-risk">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/ecommerce-risk'}
                        activeServices={activeServices}
                        next={<EcommerceRisk />}
                        handleUserLogout={handleUserLogin}
                        name="analysis"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/ecommerce-risk/:id'}
                        activeServices={activeServices}
                        next={<EcommerceRiskDetail />}
                        handleUserLogout={handleUserLogin}
                        name="analysis"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/gov-check">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/gov-check'}
                        activeServices={activeServices}
                        next={<GovCheck />}
                        handleUserLogout={handleUserLogin}
                        name="govcheck"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/gov-check/:id'}
                        activeServices={activeServices}
                        next={<GovCheckDetail />}
                        handleUserLogout={handleUserLogin}
                        name="govcheck"
                      />
                    )
                  }
                />
                <Route
                  path="manual"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/gov-check/manual'}
                        activeServices={activeServices}
                        next={<GovCheckManualCheck />}
                        handleUserLogout={handleUserLogin}
                        name="govcheckManual"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/kyt/manual">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyt/manual'}
                        activeServices={activeServices}
                        next={<KytManualCheck />}
                        handleUserLogout={handleUserLogin}
                        name="kytManualCheck"
                      />
                    )
                  }
                />
                <Route
                  path=":tab"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyt/manual/:tab'}
                        activeServices={activeServices}
                        next={<KytManualCheck />}
                        handleUserLogout={handleUserLogin}
                        name="kytManualCheck"
                      />
                    )
                  }
                />
              </Route>
              <Route
                path="/external-payment-methods"
                element={
                  !accessToken ? (
                    <Login handleUserLogin={handleUserLogin} />
                  ) : (
                    <CheckJWT
                      path={'/external-payment-methods'}
                      activeServices={activeServices}
                      next={<ExternalPaymentMethods />}
                      handleUserLogout={handleUserLogin}
                      name="externalPaymentMethods"
                    />
                  )
                }
              />
              <Route path="/external-payment-methods">
                <Route
                  path=":method/:methodId"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/external-payment-methods/:method/:methodId'}
                        activeServices={activeServices}
                        next={<ExternalPaymentMethodsDetail />}
                        handleUserLogout={handleUserLogin}
                        name="paymentmethodsdetail"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/transactions">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/transactions'}
                        activeServices={activeServices}
                        next={<Transaction />}
                        handleUserLogout={handleUserLogin}
                        name="transactions"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/transactions/:id'}
                        activeServices={activeServices}
                        next={<TransactionDetail />}
                        handleUserLogout={handleUserLogin}
                        name="transactions"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/kyt/users">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyt/users'}
                        activeServices={activeServices}
                        next={<User />}
                        handleUserLogout={handleUserLogin}
                        name="users"
                      />
                    )
                  }
                />
              </Route>
              <Route
                path="/kyt/users/:type/:id"
                element={
                  !accessToken ? (
                    <Login handleUserLogin={handleUserLogin} />
                  ) : (
                    <CheckJWT
                      path="/kyt/users/:type/:id"
                      activeServices={activeServices}
                      next={<UserDetail />}
                      handleUserLogout={handleUserLogin}
                      name="users"
                    />
                  )
                }
              />
              <Route path="/rules">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/rules'}
                        activeServices={activeServices}
                        next={<Rule />}
                        handleUserLogout={handleUserLogin}
                        name="rules"
                      />
                    )
                  }
                />
                <Route
                  path=":actualTab"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/rules'}
                        activeServices={activeServices}
                        next={<Rule />}
                        handleUserLogout={handleUserLogin}
                        name="rules"
                      />
                    )
                  }
                />
              </Route>
              {process.env.REACT_APP_IS_DEMO && (
                <Route path="/device">
                  <Route
                    index
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/device'}
                          activeServices={activeServices}
                          next={<Device />}
                          handleUserLogout={handleUserLogin}
                          name="deviceIntelligence"
                        />
                      )
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/device/:id'}
                          activeServices={activeServices}
                          next={<DeviceDetails />}
                          handleUserLogout={handleUserLogin}
                          name="deviceIntelligence"
                        />
                      )
                    }
                  />
                </Route>
              )}
              {process.env.REACT_APP_IS_DEMO && (
                <Route path="/workflows">
                  <Route
                    index
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/workflows'}
                          activeServices={activeServices}
                          next={<Workflows />}
                          handleUserLogout={handleUserLogin}
                          name="workflows"
                        />
                      )
                    }
                  />
                  <Route
                    path="options"
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/workflows/options'}
                          activeServices={activeServices}
                          next={<WorkflowsOptions />}
                          handleUserLogout={handleUserLogin}
                          name="workflowsOptions"
                        />
                      )
                    }
                  />
                  <Route
                    path="ai"
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/workflows/ai'}
                          activeServices={activeServices}
                          next={<WorkflowsAi />}
                          handleUserLogout={handleUserLogin}
                          name="workflowsIa"
                        />
                      )
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      !accessToken ? (
                        <Login handleUserLogin={handleUserLogin} />
                      ) : (
                        <CheckJWT
                          path={'/workflows/:id'}
                          activeServices={activeServices}
                          next={<WorkflowsBuilder />}
                          handleUserLogout={handleUserLogin}
                          name="workflowsBuilder"
                        />
                      )
                    }
                  />
                </Route>
              )}
              <Route path="/admin/users">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/users'}
                        activeServices={activeServices}
                        next={<Users />}
                        handleUserLogout={handleUserLogin}
                        name="dashboardUsers"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/client-consumptions">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/client-consumptions'}
                        activeServices={activeServices}
                        next={<ClientConsumptions />}
                        handleUserLogout={handleUserLogin}
                        name="client-consumptions"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/roles">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/roles'}
                        activeServices={activeServices}
                        next={<Roles />}
                        handleUserLogout={handleUserLogin}
                        name="roles"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/roles'}
                        activeServices={activeServices}
                        next={<RoleDetails />}
                        handleUserLogout={handleUserLogin}
                        name="roles"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/opposition-control">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/opposition-control'}
                        activeServices={activeServices}
                        next={<OppositionControl />}
                        handleUserLogout={handleUserLogin}
                        name="oppositionControl"
                      />
                    )
                  }
                />
                <Route
                  path=":requestId"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/opposition-control/:requestId'}
                        activeServices={activeServices}
                        next={<OppositionControl />}
                        handleUserLogout={handleUserLogin}
                        name="oppositionControl"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/security">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/security'}
                        activeServices={activeServices}
                        next={<SecurityParams />}
                        handleUserLogout={handleUserLogin}
                        name="securityParameters"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/risk-matrix">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/risk-matrix'}
                        activeServices={activeServices}
                        next={<RiskMatrix />}
                        handleUserLogout={handleUserLogin}
                        name="riskMatrix"
                      />
                    )
                  }
                />
                <Route
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/risk-matrix'}
                        activeServices={activeServices}
                        next={<RiskMatrixCreation />}
                        handleUserLogout={handleUserLogin}
                        name="riskMatrix"
                      />
                    )
                  }
                />
                <Route
                  path="creation"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/risk-matrix'}
                        activeServices={activeServices}
                        next={<RiskMatrixCreation />}
                        handleUserLogout={handleUserLogin}
                        name="riskMatrix"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/kyt-import">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/kyt-import'}
                        activeServices={activeServices}
                        next={<CsvPage />}
                        handleUserLogout={handleUserLogin}
                        name="kyt-import"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/totalizadores">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/totalizadores'}
                        activeServices={activeServices}
                        next={<Totalizadores />}
                        handleUserLogout={handleUserLogin}
                        name="admin"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/webhooks">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/webhooks'}
                        activeServices={activeServices}
                        next={<Webhooks />}
                        handleUserLogout={handleUserLogin}
                        name="webhooks"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/lists">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/lists'}
                        activeServices={activeServices}
                        next={<Lists />}
                        handleUserLogout={handleUserLogin}
                        name="lists"
                      />
                    )
                  }
                />
                <Route
                  path=":tab"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/lists'}
                        activeServices={activeServices}
                        next={<Lists />}
                        handleUserLogout={handleUserLogin}
                        name="lists"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/reports">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/reports'}
                        activeServices={activeServices}
                        next={<Reports />}
                        handleUserLogout={handleUserLogin}
                        name="reports"
                      />
                    )
                  }
                />
                <Route
                  path=":reportKey"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/reports'}
                        activeServices={activeServices}
                        next={<ReportRouter />}
                        handleUserLogout={handleUserLogin}
                        name="reports"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/backoffice">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/backoffice'}
                        activeServices={activeServices}
                        next={<BackOffice />}
                        handleUserLogout={handleUserLogin}
                        name="backoffice"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/coming-soon">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/coming-soon'}
                        activeServices={activeServices}
                        next={<ComingSoon />}
                        handleUserLogout={handleUserLogin}
                      />
                    )
                  }
                />
              </Route>
              <Route path="/admin/alerts">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/admin/alerts'}
                        activeServices={activeServices}
                        next={<AlertSettings />}
                        handleUserLogout={handleUserLogin}
                        name="alertSettings"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/profile">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        activeServices={activeServices}
                        next={<Profile />}
                        handleUserLogout={handleUserLogin}
                        name="profile"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/batch">
                <Route
                  index
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/'}
                        activeServices={activeServices}
                        next={<Batch />}
                        name="batch"
                        handleUserLogout={handleUserLogin}
                      />
                    )
                  }
                />
              </Route>
              <Route path="/batch-details">
                <Route
                  index
                  path=":id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/'}
                        activeServices={activeServices}
                        next={<BatchDetails />}
                        name="batchDetails"
                        handleUserLogout={handleUserLogin}
                      />
                    )
                  }
                />
              </Route>
              <Route path="/kyc">
                <Route
                  path="persons"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyc/persons'}
                        activeServices={activeServices}
                        next={<Kyc />}
                        handleUserLogout={handleUserLogin}
                        name="kycPersons"
                      />
                    )
                  }
                />
                <Route
                  path="checks"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyc/checks'}
                        activeServices={activeServices}
                        next={<KycChecks />}
                        handleUserLogout={handleUserLogin}
                        name="kycChecks"
                      />
                    )
                  }
                />
                <Route
                  path="person/:id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyc/person/:id'}
                        activeServices={activeServices}
                        next={<KycPerson />}
                        handleUserLogout={handleUserLogin}
                        name="kycPersons"
                      />
                    )
                  }
                />
                <Route
                  path="person/:id/:checkType/*"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kyc/person/:id/:checkType/:checkId'}
                        activeServices={activeServices}
                        next={<KycPerson />}
                        handleUserLogout={handleUserLogin}
                        name="kycPersons"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/kycv2">
                <Route
                  path="persons"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kycv2/persons'}
                        activeServices={activeServices}
                        next={<KycV2 />}
                        handleUserLogout={handleUserLogin}
                        name="kycv2Persons"
                      />
                    )
                  }
                />
                <Route
                  path="person/:id"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kycv2/person/:id'}
                        activeServices={activeServices}
                        next={<KycV2Person />}
                        handleUserLogout={handleUserLogin}
                        name="kycv2Persons"
                      />
                    )
                  }
                />
                <Route
                  path="session/:sessionId"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path={'/kycv2/session/:sessionId'}
                        activeServices={activeServices}
                        next={<KycV2Session />}
                        handleUserLogout={handleUserLogin}
                        name="kycv2Persons"
                      />
                    )
                  }
                />
              </Route>
              <Route path="/kym">
                <Route
                  path="persons"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path="/kym/persons"
                        activeServices={activeServices}
                        next={<KymPersons />}
                        handleUserLogout={handleUserLogin}
                        name="kymPersons"
                      />
                    )
                  }
                />
                <Route
                  path="business"
                  element={
                    !accessToken ? (
                      <Login handleUserLogin={handleUserLogin} />
                    ) : (
                      <CheckJWT
                        path="/kym/business"
                        activeServices={activeServices}
                        next={<KymBusiness />}
                        handleUserLogout={handleUserLogin}
                        name="kymBusiness"
                      />
                    )
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
          <ToastContainer
            autoClose={2500}
            draggable={false}
            position="bottom-center"
            closeButton={false}
            toastStyle={{ background: 'transparent', boxShadow: 'none' }}
            hideProgressBar
            stacked
          />
        </ThemeProvider>
      </LoadScript>
    </Suspense>
  )
}

export default App
